import {
  Callout,
  Checkbox,
  Classes,
  FormGroup,
  HTMLSelect,
  InputGroup,
  Tag,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import * as React from 'react';
import { useQueryClient } from 'react-query';
import { useIsMounted } from 'vet-bones/utils/hooks';

import { Dialog } from 'src/ui/components/Dialog';
import { FormAction, FormActions } from 'src/ui/components/FormActions';
import { Loading } from 'src/ui/components/Loading';
import {
  CLOUD_REPORT_TRANSFER_ACCESS_ACTION,
  CLOUD_REPORT_TRANSFER_CARRIED_LIST,
  CLOUD_REPORT_TRANSFER_DESTINATION_NEW,
  CLOUD_REPORT_TRANSFER_DESTINATION_SELECT,
  CLOUD_REPORT_TRANSFER_DIALOG,
  CLOUD_REPORT_TRANSFER_INELIGIBLE,
  CLOUD_REPORT_TRANSFER_OTHER_LIST,
  CLOUD_REPORT_TRANSFER_OWNER,
  CLOUD_REPORT_TRANSFER_TARGET_USER_INPUT,
  CLOUD_REPORT_TRANSFER_USER_NOT_FOUND,
} from 'src/ui/constants/testIds';
import { makeSubmitAction } from 'src/ui/containers/dialogs/makeSubmitAction';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import {
  TransferAccessAction,
  useGetCloudTransferPreviewQuery,
  useGetCloudTransferTargetQuery,
  useTransferCloudToUserMutation,
} from 'src/ui/graph/types';
import { useOnError, useOnMutate } from 'src/ui/hooks/graph';
import * as copy from 'src/ui/templates/copy';

const dialogCopy = copy.components.transferCloudToUserDialog;

const DATABRICKS_LINKED_REASON = 'DATABRICKS_LINKED';
const ACTION_OPTIONS = [
  { label: dialogCopy.actionCarry, value: TransferAccessAction.Carry },
  { label: dialogCopy.actionDrop, value: TransferAccessAction.Drop },
];

type TransferCloudToUserDialogProps = {
  cloudId: string;
  isOpen: boolean;
  onClose: () => void;
};

type AccessEntry = {
  email: string;
  roles: string[];
  accepted: boolean;
};

type RawAccess = {
  email?: string | null;
  // eslint-disable-next-line camelcase
  stardog_roles?: Array<string | null> | null;
  accepted?: boolean | null;
} | null;

const toAccessEntries = (entries?: RawAccess[] | null): AccessEntry[] =>
  (entries ?? []).flatMap((entry) =>
    entry?.email
      ? [
          {
            email: entry.email,
            roles: (entry.stardog_roles ?? []).filter(Boolean) as string[],
            accepted: !!entry.accepted,
          },
        ]
      : []
  );

const AccessItem: React.VFC<{
  entry: AccessEntry;
  control?: React.ReactNode;
}> = ({ entry, control }) => (
  <div className="cloud-report__move-access-item">
    {entry.email}{' '}
    <Tag minimal>
      {entry.accepted ? dialogCopy.accepted : dialogCopy.pending}
    </Tag>{' '}
    {entry.roles.map((role) => (
      <Tag intent="primary" key={role} minimal>
        {role}
      </Tag>
    ))}
    {control}
  </div>
);

export const TransferCloudToUserDialog: React.VFC<TransferCloudToUserDialogProps> = ({
  cloudId,
  isOpen,
  onClose,
}) => {
  const isMounted = useIsMounted();
  const onMutate = useOnMutate();
  const onError = useOnError();
  const queryClient = useQueryClient();

  const [targetUser, setTargetUser] = React.useState('');
  const [checkedEmail, setCheckedEmail] = React.useState<string | null>(null);
  const [destinationId, setDestinationId] = React.useState<string | null>(null);
  const [transferOwner, setTransferOwner] = React.useState(true);
  const [accessActions, setAccessActions] = React.useState<
    Record<string, string>
  >({});

  const { data, isLoading } = useGetCloudTransferPreviewQuery(
    graphQLClient,
    { cloud_id: cloudId },
    { enabled: isOpen }
  );
  const preview = data?.getCloudTransferPreview;

  const carriedAccess = React.useMemo(
    () => toAccessEntries(preview?.carried_access),
    [preview]
  );

  // Default every carried email to CARRY; prune emails no longer present.
  React.useEffect(() => {
    const emails = carriedAccess.map((entry) => entry.email);
    setAccessActions((prev) =>
      Object.fromEntries(
        emails.map((email) => [
          email,
          email in prev ? prev[email] : TransferAccessAction.Carry,
        ])
      )
    );
  }, [carriedAccess]);
  const otherAccess = React.useMemo(
    () => toAccessEntries(preview?.other_access),
    [preview]
  );
  const otherConnections = (preview?.other_connections ?? []).filter(
    Boolean
  ) as string[];

  const trimmedEmail = targetUser.trim();

  const targetQuery = useGetCloudTransferTargetQuery(
    graphQLClient,
    { cloud_id: cloudId, email: checkedEmail ?? '' },
    { enabled: !!checkedEmail }
  );
  // A lookup result is only "current" when it matches the typed email.
  const target =
    checkedEmail === trimmedEmail
      ? targetQuery.data?.getCloudTransferTarget
      : undefined;
  const isChecking = checkedEmail === trimmedEmail && targetQuery.isFetching;
  const userExists = !!target?.user_exists;
  const candidates = React.useMemo(
    () => (target?.candidates ?? []).filter(Boolean),
    [target]
  );
  const needsChoice = userExists && candidates.length > 1;

  const handleClose = () => {
    setTargetUser('');
    setCheckedEmail(null);
    setDestinationId(null);
    setTransferOwner(true);
    setAccessActions({});
    onClose();
  };

  const {
    isLoading: isTransferring,
    mutate: transferCloud,
  } = useTransferCloudToUserMutation(graphQLClient, {
    onMutate,
    onError,
    onSuccess: async (response) => {
      if (!response.transferCloudToUser?.success) {
        return;
      }
      /* istanbul ignore if */
      if (!isMounted.current) {
        return;
      }
      await queryClient.refetchQueries(['getCloudReport']);
      /* istanbul ignore if */
      if (!isMounted.current) {
        return;
      }
      handleClose();
    },
  });

  const isEligible = preview ? preview.eligible !== false : true;
  const ineligibleMessage =
    preview?.ineligible_reason === DATABRICKS_LINKED_REASON
      ? dialogCopy.ineligibleDatabricks
      : dialogCopy.ineligible;
  const isWorking = userExists ? isTransferring : isChecking;
  const isSubmitDisabled =
    !trimmedEmail ||
    !isEligible ||
    isLoading ||
    isWorking ||
    (needsChoice && !destinationId);

  const handleSubmit = (evt: React.FormEvent) => {
    evt.preventDefault();

    if (isSubmitDisabled) {
      return;
    }

    // Phase 1: resolve the target user and their destination candidates.
    if (!userExists) {
      if (checkedEmail === trimmedEmail) {
        targetQuery.refetch();
      } else {
        setCheckedEmail(trimmedEmail);
        setDestinationId(null);
      }
      return;
    }

    // Phase 2: commit the transfer. Only send a destination when the user must
    // choose among several; a single candidate (or none) is resolved server-side.
    transferCloud({
      input: {
        cloud_id: cloudId,
        target_user_email: trimmedEmail,
        destination_connection_id: needsChoice ? destinationId : undefined,
        transfer_owner: transferOwner,
        access_actions: carriedAccess.map((entry) => ({
          email: entry.email,
          action:
            (accessActions[entry.email] as TransferAccessAction) ??
            TransferAccessAction.Carry,
        })),
      },
    });
  };

  const actions: FormAction[] = [
    {
      text: dialogCopy.cancel,
      onClick: handleClose,
    },
    makeSubmitAction(
      userExists ? dialogCopy.submit : dialogCopy.checkUser,
      isWorking
    ),
  ];

  let submitTooltipText = '';
  if (!trimmedEmail) {
    submitTooltipText = dialogCopy.targetUserRequired;
  }

  let destinationSection: React.ReactNode = null;
  if (userExists) {
    if (candidates.length === 0) {
      destinationSection = (
        <Callout
          data-testid={CLOUD_REPORT_TRANSFER_DESTINATION_NEW}
          intent="primary"
        >
          {dialogCopy.destinationNew}
        </Callout>
      );
    } else if (candidates.length > 1) {
      destinationSection = (
        <FormGroup
          helperText={dialogCopy.destinationHelp}
          label={dialogCopy.destination}
        >
          <HTMLSelect
            data-testid={CLOUD_REPORT_TRANSFER_DESTINATION_SELECT}
            disabled={isTransferring}
            onChange={(evt) =>
              setDestinationId(evt.currentTarget.value || null)
            }
            value={destinationId ?? ''}
          >
            <option value="">{dialogCopy.destinationChoose}</option>
            {candidates.map((candidate) => (
              <option
                key={candidate?.connection_id ?? ''}
                value={candidate?.connection_id ?? ''}
              >
                {candidate?.connection_name}
              </option>
            ))}
          </HTMLSelect>
        </FormGroup>
      );
    }
  } else if (target && !userExists) {
    destinationSection = (
      <p
        className={Classes.TEXT_MUTED}
        data-testid={CLOUD_REPORT_TRANSFER_USER_NOT_FOUND}
      >
        {dialogCopy.userNotFound}
      </p>
    );
  }

  let body: React.ReactNode;
  if (isLoading) {
    body = <Loading />;
  } else if (!isEligible) {
    body = (
      <p data-testid={CLOUD_REPORT_TRANSFER_INELIGIBLE}>{ineligibleMessage}</p>
    );
  } else {
    body = (
      <form className="cloud-report__move-form" onSubmit={handleSubmit}>
        <FormGroup
          helperText={dialogCopy.targetUserHelp}
          label={dialogCopy.targetUser}
        >
          <InputGroup
            autoFocus
            data-testid={CLOUD_REPORT_TRANSFER_TARGET_USER_INPUT}
            disabled={isTransferring}
            onChange={(evt) => setTargetUser(evt.target.value)}
            value={targetUser}
          />
        </FormGroup>

        <div className="cloud-report__move-content">
          {destinationSection}

          <FormGroup helperText={dialogCopy.transferOwnerHelp}>
            <Checkbox
              checked={transferOwner}
              data-testid={CLOUD_REPORT_TRANSFER_OWNER}
              disabled={isTransferring}
              onChange={(evt) => setTransferOwner(evt.currentTarget.checked)}
            >
              {dialogCopy.transferOwner}
            </Checkbox>
          </FormGroup>

          <FormGroup label={dialogCopy.carriedHeading}>
            {!carriedAccess.length ? (
              <p className={Classes.TEXT_MUTED}>{dialogCopy.carriedEmpty}</p>
            ) : (
              <div
                className="cloud-report__move-list"
                data-testid={CLOUD_REPORT_TRANSFER_CARRIED_LIST}
              >
                {carriedAccess.map((entry, idx) => (
                  <AccessItem
                    control={
                      <HTMLSelect
                        data-testid={CLOUD_REPORT_TRANSFER_ACCESS_ACTION(
                          entry.email,
                          idx
                        )}
                        disabled={isTransferring}
                        onChange={(evt) => {
                          const { value } = evt.currentTarget;
                          setAccessActions((prev) => ({
                            ...prev,
                            [entry.email]: value,
                          }));
                        }}
                        options={ACTION_OPTIONS}
                        value={
                          accessActions[entry.email] ??
                          TransferAccessAction.Carry
                        }
                      />
                    }
                    entry={entry}
                    key={entry.email}
                  />
                ))}
              </div>
            )}
          </FormGroup>

          {otherAccess.length || otherConnections.length ? (
            <FormGroup
              helperText={dialogCopy.otherHelp}
              label={dialogCopy.otherHeading}
            >
              <div
                className="cloud-report__move-list"
                data-testid={CLOUD_REPORT_TRANSFER_OTHER_LIST}
              >
                {otherConnections.length ? (
                  <p className={Classes.TEXT_MUTED}>
                    {dialogCopy.otherConnections(otherConnections.join(', '))}
                  </p>
                ) : null}
                {otherAccess.map((entry) => (
                  <AccessItem entry={entry} key={entry.email} />
                ))}
              </div>
            </FormGroup>
          ) : null}
        </div>

        <input hidden type="submit" />
      </form>
    );
  }

  return (
    <Dialog
      canEscapeKeyClose={!isTransferring}
      canOutsideClickClose={!isTransferring}
      className="cloud-report__move-dialog"
      icon={IconNames.EXCHANGE}
      isOpen={isOpen}
      onClose={handleClose}
      title={dialogCopy.title}
    >
      <div
        className={Classes.DIALOG_BODY}
        data-testid={CLOUD_REPORT_TRANSFER_DIALOG}
      >
        {body}
      </div>
      <FormActions
        actions={actions}
        handleSubmit={handleSubmit}
        isSubmitDisabled={isSubmitDisabled}
        submitTooltipText={submitTooltipText}
      />
    </Dialog>
  );
};
